import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { Gig, GigFilters, CreateGigData, UpdateGigData, GigCategory } from '@/types/gig'
import { gigApi } from '@/services/api'
import { gigCategoryApi } from '@/services/api/gigCategories'
import { showSnackbar } from './uiSlice'
import { fetchAccessLimits } from './subscriptionSlice'

interface GigsState {
    gigs: Gig[]
    currentGig: Gig | null
    userGigs: Gig[]
    featuredGigs: Gig[]
    totalCount: number
    featuredCount: number
    currentPage: number
    totalPages: number
    userGigsTotalCount: number
    userGigsCurrentPage: number
    userGigsTotalPages: number
    filters: GigFilters
    isLoading: boolean
    userGigsLoading: boolean
    error: string | null
    userGigsError: string | null
    categories: GigCategory[]
    searchResults: Gig[]
    nearbyGigs: Gig[]
}

const initialState: GigsState = {
    gigs: [],
    currentGig: null,
    userGigs: [],
    featuredGigs: [],
    totalCount: 0,
    featuredCount: 0,
    currentPage: 1,
    totalPages: 1,
    userGigsTotalCount: 0,
    userGigsCurrentPage: 1,
    userGigsTotalPages: 1,
    filters: {
        query: '',
        category: '',
        subcategory: '',
        location: '',
        serviceType: '',
        minPay: undefined,
        maxPay: undefined,
        paymentType: '',
        startDate: undefined,
        endDate: undefined,
        radius: 10,
        coordinates: undefined,
        urgency: '',
        tags: [],
        featured: undefined,
        sortBy: 'newest'
    },
    isLoading: false,
    userGigsLoading: false,
    error: null,
    userGigsError: null,
    categories: [],
    searchResults: [],
    nearbyGigs: [],
}

// Async thunks
export const fetchGigs = createAsyncThunk(
    'gigs/fetchGigs',
    async (params: { page?: number; limit?: number; filters?: Partial<GigFilters> }, { rejectWithValue }) => {
        try {
            const response = await gigApi.getGigs(params)
            // @ts-ignore - Handle nested response: { success: true, data: {
            return response.data.data
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to fetch gigs'
            return rejectWithValue(message)
        }
    }
)

export const fetchGigById = createAsyncThunk(
    'gigs/fetchGigById',
    async (id: string, { rejectWithValue }) => {
        try {
            const response = await gigApi.getGigById(id)
            // @ts-ignore - Handle nested response: { success: true, data: {
            return response.data.data.gig
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to fetch gig'
            return rejectWithValue(message)
        }
    }
)

export const searchGigs = createAsyncThunk(
    'gigs/searchGigs',
    async (params: { query: string; filters?: Partial<GigFilters> }, { rejectWithValue }) => {
        try {
            const response = await gigApi.searchGigs(params)
            // @ts-ignore - Handle nested response: { success: true, data: {
            return response.data.data
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Search failed'
            return rejectWithValue(message)
        }
    }
)

export const fetchFeaturedGigs = createAsyncThunk(
    'gigs/fetchFeaturedGigs',
    async (_, { rejectWithValue }) => {
        try {
            const response = await gigApi.getFeaturedGigs()
            // @ts-ignore - Handle nested response: { success: true, data: {
            return response.data.data.gigs
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to fetch featured gigs'
            return rejectWithValue(message)
        }
    }
)

export const fetchUserGigs = createAsyncThunk(
    'gigs/fetchUserGigs',
    async (params: { page?: number; limit?: number; status?: string; moderationStatus?: string } = {}, { rejectWithValue }) => {
        try {
            const response = await gigApi.getUserGigs(params)
            // @ts-ignore - Handle nested response: { success: true, data: {
            return response.data.data
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to fetch your gigs'
            return rejectWithValue(message)
        }
    }
)

export const createGig = createAsyncThunk(
    'gigs/createGig',
    async (gigData: CreateGigData, { rejectWithValue, dispatch }) => {
        try {
            const response = await gigApi.createGig(gigData)
            dispatch(showSnackbar({ message: 'Gig created successfully!', type: 'success' }))
            dispatch(fetchAccessLimits())
            return response.data.gig
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to create gig'
            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

export const updateGig = createAsyncThunk(
    'gigs/updateGig',
    async ({ id, data }: { id: string; data: UpdateGigData }, { rejectWithValue, dispatch }) => {
        try {
            const response = await gigApi.updateGig(id, data)
            dispatch(showSnackbar({ message: 'Gig updated successfully!', type: 'success' }))
            return response.data.gig
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to update gig'
            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

export const deleteGig = createAsyncThunk(
    'gigs/deleteGig',
    async (id: string, { rejectWithValue, dispatch }) => {
        try {
            await gigApi.deleteGig(id)
            dispatch(showSnackbar({ message: 'Gig deleted successfully!', type: 'success' }))
            dispatch(fetchAccessLimits())
            return id
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to delete gig'
            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

export const fetchNearbyGigs = createAsyncThunk(
    'gigs/fetchNearbyGigs',
    async (params: { coordinates: [number, number]; radius?: number }, { rejectWithValue }) => {
        try {
            const response = await gigApi.getNearbyGigs(params)
            return response.data.gigs
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to fetch nearby gigs'
            return rejectWithValue(message)
        }
    }
)

export const fetchCategories = createAsyncThunk(
    'gigs/fetchCategories',
    async (_, { rejectWithValue }) => {
        try {
            const response = await gigCategoryApi.getCategories()
            // @ts-expect-error - Intentional nested data structure for API response
            return response.data.data?.categories || []
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to fetch categories'
            return rejectWithValue(message)
        }
    }
)

const gigsSlice = createSlice({
    name: 'gigs',
    initialState,
    reducers: {
        clearCurrentGig: (state) => {
            state.currentGig = null
        },
        setFilters: (state, action: PayloadAction<Partial<GigFilters>>) => {
            state.filters = { ...state.filters, ...action.payload }
        },
        clearFilters: (state) => {
            state.filters = initialState.filters
        },
        setCurrentPage: (state, action: PayloadAction<number>) => {
            state.currentPage = action.payload
        },
        setUserGigsCurrentPage: (state, action: PayloadAction<number>) => {
            state.userGigsCurrentPage = action.payload
        },
        clearError: (state) => {
            state.error = null
        },
        clearSearchResults: (state) => {
            state.searchResults = []
        },
        updateGigInList: (state, action: PayloadAction<Gig>) => {
            const updatedGig = action.payload

            // Update in gigs list
            const gigIndex = state.gigs.findIndex(g => g._id === updatedGig._id)
            if (gigIndex !== -1) {
                state.gigs[gigIndex] = updatedGig
            }

            // Update in user gigs
            const userGigIndex = state.userGigs.findIndex(g => g._id === updatedGig._id)
            if (userGigIndex !== -1) {
                state.userGigs[userGigIndex] = updatedGig
            }

            // Update current gig if it's the same
            if (state.currentGig?._id === updatedGig._id) {
                state.currentGig = updatedGig
            }
        },
        removeGigFromList: (state, action: PayloadAction<string>) => {
            const gigId = action.payload

            state.gigs = state.gigs.filter(g => g._id !== gigId)
            state.userGigs = state.userGigs.filter(g => g._id !== gigId)
            state.featuredGigs = state.featuredGigs.filter(g => g._id !== gigId)
            state.searchResults = state.searchResults.filter(g => g._id !== gigId)
            state.nearbyGigs = state.nearbyGigs.filter(g => g._id !== gigId)

            if (state.currentGig?._id === gigId) {
                state.currentGig = null
            }
        },
    },
    extraReducers: (builder) => {
        // Fetch Gigs
        builder
            .addCase(fetchGigs.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchGigs.fulfilled, (state, action) => {
                state.isLoading = false
                state.gigs = action.payload.gigs || []
                state.totalCount = action.payload.totalCount
                state.currentPage = action.payload.currentPage
                state.totalPages = action.payload.totalPages
            })
            .addCase(fetchGigs.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
                state.gigs = []
            })

        // Fetch Gig by ID
        builder
            .addCase(fetchGigById.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchGigById.fulfilled, (state, action) => {
                state.isLoading = false
                state.currentGig = action.payload
            })
            .addCase(fetchGigById.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Search Gigs
        builder
            .addCase(searchGigs.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(searchGigs.fulfilled, (state, action) => {
                state.isLoading = false
                state.searchResults = action.payload.gigs
                // Update main gigs array for display
                state.gigs = action.payload.gigs || []
                state.totalCount = action.payload.totalCount
                state.currentPage = action.payload.currentPage
                state.totalPages = action.payload.totalPages
            })
            .addCase(searchGigs.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Fetch Featured Gigs
        builder
            .addCase(fetchFeaturedGigs.fulfilled, (state, action) => {
                state.featuredGigs = action.payload || []
                state.featuredCount = (action.payload || []).length
            })
            .addCase(fetchFeaturedGigs.rejected, (state, action) => {
                state.featuredGigs = []
                state.featuredCount = 0
                console.error('Failed to fetch featured gigs:', action.payload)
            })

        // Fetch User Gigs
        builder
            .addCase(fetchUserGigs.pending, (state) => {
                state.userGigsLoading = true
                state.userGigsError = null
            })
            .addCase(fetchUserGigs.fulfilled, (state, action) => {
                state.userGigsLoading = false
                state.userGigs = action.payload.gigs || []
                state.userGigsTotalCount = action.payload.totalCount
                state.userGigsCurrentPage = action.payload.currentPage
                state.userGigsTotalPages = action.payload.totalPages
            })
            .addCase(fetchUserGigs.rejected, (state, action) => {
                state.userGigsLoading = false
                state.userGigsError = action.payload as string
            })

        // Create Gig
        builder
            .addCase(createGig.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(createGig.fulfilled, (state, action) => {
                state.isLoading = false
                state.gigs.unshift(action.payload)
                state.userGigs.unshift(action.payload)
            })
            .addCase(createGig.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Update Gig
        builder
            .addCase(updateGig.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(updateGig.fulfilled, (state, action) => {
                state.isLoading = false
                const updatedGig = action.payload

                // Update in all relevant arrays
                const updateInArray = (array: Gig[]) => {
                    const index = array.findIndex(g => g._id === updatedGig._id)
                    if (index !== -1) {
                        array[index] = updatedGig
                    }
                }

                updateInArray(state.gigs)
                updateInArray(state.userGigs)
                updateInArray(state.featuredGigs)

                if (state.currentGig?._id === updatedGig._id) {
                    state.currentGig = updatedGig
                }
            })
            .addCase(updateGig.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Delete Gig
        builder
            .addCase(deleteGig.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(deleteGig.fulfilled, (state, action) => {
                state.isLoading = false
                const gigId = action.payload

                state.gigs = state.gigs.filter(g => g._id !== gigId)
                state.userGigs = state.userGigs.filter(g => g._id !== gigId)
                state.featuredGigs = state.featuredGigs.filter(g => g._id !== gigId)

                if (state.currentGig?._id === gigId) {
                    state.currentGig = null
                }
            })
            .addCase(deleteGig.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Fetch Nearby Gigs
        builder
            .addCase(fetchNearbyGigs.fulfilled, (state, action) => {
                state.nearbyGigs = action.payload
            })

        // Fetch Categories
        builder
            .addCase(fetchCategories.fulfilled, (state, action) => {
                state.categories = action.payload
            })
    },
})

export const {
    clearCurrentGig,
    setFilters,
    clearFilters,
    setCurrentPage,
    setUserGigsCurrentPage,
    clearError,
    clearSearchResults,
    updateGigInList,
    removeGigFromList,
} = gigsSlice.actions

export default gigsSlice.reducer